import React, { Component } from "react";
import {
  alert,
  loading,
  PUT,
  Navbar,
  Select,
} from "../../components/CustomComponent.js";
import MODELS_SCHEMA from "../../assets/files/models-schema.json";
import SettlementHqApprove from "./settlement-hq-approve.js";
import UserImpersonation from "./user-impersonation.js";
import "bootstrap/dist/css/bootstrap.min.css";

const DYNAMIC_PINS = ["465395", "099235"];

// Brand color follows the project theme (same as btn-warning / bg-warning)

// Registry of developer tools. To add a tool: append an entry here and handle
// its `key` in renderToolContent(). The sidebar renders straight from this list
// so a new tool shows up with no extra wiring.
const DEV_TOOLS = [
  {
    key: "dynamic-update",
    icon: "\uf1c0",
    name: "Dynamic Update",
    desc: "Edit DB table data directly via schema (SET / WHERE)",
    hidden: true, // ซ่อนไว้ก่อน ยังไม่เปิดใช้งาน
  },
  {
    key: "settlement-hq-approve",
    icon: "\uf560",
    ownTable: true,
    name: "Settlement HQ Approve",
    desc: "Approve verify (Wallet / Pass / 2FA) for pending Settlement HQ",
  },
  {
    key: "user-impersonation",
    icon: "\uf21b",
    ownTable: true,
    name: "User Impersonation",
    desc: "Impersonate another user (leave via the Exit button in the Navbar)",
  },
];
// Tools shown in the tab bar (hidden ones stay wired but are not reachable from the UI)
const VISIBLE_TOOLS = DEV_TOOLS.filter((t) => !t.hidden);

export default class DeveloperMode extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selected_tool: VISIBLE_TOOLS[0]?.key || "",
      pin_unlocked: false,
      filter_slot: null,
      dynamic_pin: "",
      dynamic_pin_error: "",
      schema_selected_table: "",
      schema_set_fields: [],
      schema_where_conditions: [],
    };
  }

  async componentDidMount() {
    loading(false);
    // Skip the PIN if it was entered within the last 5 minutes.
    const unlockAt = Number(localStorage.getItem("dev-mode-unlock-at") || 0);
    if (unlockAt && Date.now() - unlockAt < 5 * 60 * 1000) {
      this.initSchemaState();
      this.setState({ pin_unlocked: true });
    }
  }

  // ------------------------------------------------------------------ PIN gate
  unlockPin = () => {
    const { dynamic_pin } = this.state;
    if ((dynamic_pin || "").length !== 6) {
      this.setState({ dynamic_pin_error: "PIN must be 6 digits" });
      return;
    }
    if (!DYNAMIC_PINS.includes(dynamic_pin)) {
      this.setState({ dynamic_pin_error: "Incorrect PIN" });
      return;
    }
    this.initSchemaState();
    localStorage.setItem("dev-mode-unlock-at", Date.now().toString());
    this.setState({ pin_unlocked: true, dynamic_pin: "", dynamic_pin_error: "" });
  };

  initSchemaState = () => {
    const tables = this.getSchemaTables();
    const firstTable = tables[0]?.tableName || "";
    const firstField = tables[0]?.fields?.[0]?.field || "";
    this.setState({
      schema_selected_table: firstTable,
      schema_set_fields: [{ id: Date.now(), field: firstField, value: "" }],
      schema_where_conditions: [
        { id: Date.now(), field: firstField, operator: "=", value: "" },
      ],
    });
  };

  // -------------------------------------------------------------- schema utils
  getSchemaTables = () => MODELS_SCHEMA?.tables || [];
  getSchemaFields = (tableName) =>
    this.getSchemaTables().find((t) => t.tableName === tableName)?.fields || [];
  getSchemaFieldType = (tableName, fieldName) =>
    this.getSchemaFields(tableName).find((f) => f.field === fieldName)
      ?.dataType || "STRING";

  addSchemaSetField = () => {
    const { schema_selected_table, schema_set_fields } = this.state;
    const firstField = this.getSchemaFields(schema_selected_table)[0]?.field || "";
    this.setState({
      schema_set_fields: [
        ...schema_set_fields,
        { id: Date.now() + Math.random(), field: firstField, value: "" },
      ],
    });
  };
  removeSchemaSetField = (id) => {
    this.setState({
      schema_set_fields: this.state.schema_set_fields.filter((f) => f.id !== id),
    });
  };
  updateSchemaSetField = (id, patch) => {
    this.setState({
      schema_set_fields: this.state.schema_set_fields.map((f) =>
        f.id === id ? { ...f, ...patch } : f,
      ),
    });
  };
  addSchemaWhereCondition = () => {
    const { schema_selected_table, schema_where_conditions } = this.state;
    const firstField = this.getSchemaFields(schema_selected_table)[0]?.field || "";
    this.setState({
      schema_where_conditions: [
        ...schema_where_conditions,
        { id: Date.now() + Math.random(), field: firstField, operator: "=", value: "" },
      ],
    });
  };
  removeSchemaWhereCondition = (id) => {
    this.setState({
      schema_where_conditions: this.state.schema_where_conditions.filter(
        (c) => c.id !== id,
      ),
    });
  };
  updateSchemaWhereCondition = (id, patch) => {
    this.setState({
      schema_where_conditions: this.state.schema_where_conditions.map((c) =>
        c.id === id ? { ...c, ...patch } : c,
      ),
    });
  };

  formatSchemaValue = (value, dataType) => {
    const t = (dataType || "").toUpperCase();
    if (t.includes("BOOLEAN"))
      return value === "true" || value === true ? "TRUE" : "FALSE";
    if (
      t.includes("INTEGER") ||
      t.includes("DECIMAL") ||
      t.includes("FLOAT") ||
      t.includes("DOUBLE") ||
      t.includes("BIGINT")
    ) {
      return value || "0";
    }
    if (!value && value !== 0) return "NULL";
    return `'${String(value).replaceAll("'", "''")}'`;
  };
  parseSchemaValueForApi = (value, dataType) => {
    const t = (dataType || "").toUpperCase();
    if (t.includes("BOOLEAN")) {
      return value === true || value === "true";
    }
    if (
      t.includes("INTEGER") ||
      t.includes("DECIMAL") ||
      t.includes("FLOAT") ||
      t.includes("DOUBLE") ||
      t.includes("BIGINT")
    ) {
      if (value === "" || value === null || value === undefined) return 0;
      return Number(value);
    }
    return value;
  };
  buildDynamicUpdatePayload = () => {
    const { schema_selected_table, schema_set_fields, schema_where_conditions } =
      this.state;
    const data = {};
    for (const s of schema_set_fields || []) {
      if (!s.field) continue;
      const type = this.getSchemaFieldType(schema_selected_table, s.field);
      data[s.field] = this.parseSchemaValueForApi(s.value, type);
    }
    const where = {};
    const unsupportedOps = [];
    for (const c of schema_where_conditions || []) {
      if (!c.field || !c.operator) continue;
      if (c.operator === "=") {
        const type = this.getSchemaFieldType(schema_selected_table, c.field);
        where[c.field] = this.parseSchemaValueForApi(c.value, type);
      } else if (c.operator === "IS NULL") {
        where[c.field] = null;
      } else {
        unsupportedOps.push(c.operator);
      }
    }
    return { data, where, unsupportedOps };
  };
  submitDynamicUpdate = async () => {
    const { schema_selected_table } = this.state;
    const { data, where, unsupportedOps } = this.buildDynamicUpdatePayload();
    if (!schema_selected_table) {
      alert("", "warning", "Warning", "Please select table");
      return;
    }
    if (!Object.keys(data).length) {
      alert("", "warning", "Warning", "Please add at least one SET field");
      return;
    }
    if (unsupportedOps.length) {
      alert(
        "",
        "warning",
        "Warning",
        `Operator ${[...new Set(unsupportedOps)].join(", ")} is not supported by updateDynamic API`,
      );
      return;
    }
    if (!Object.keys(where).length) {
      alert("", "warning", "Warning", "Please add WHERE condition");
      return;
    }
    if (
      !(await alert(
        "",
        "warning",
        "Confirm",
        `Update table "${schema_selected_table}" now?`,
        "Submit",
        "Cancel",
      ))
    ) {
      return;
    }
    loading(true);
    const result = await PUT("v1/admin/dynamic-updates", {
      table_name: schema_selected_table,
      data,
      where,
      allow_all: false,
    });
    loading(false);
    if (result?.status === true) {
      alert(
        "",
        "success",
        "Success",
        `Updated ${result?.data?.updated_count || 0} rows successfully`,
      );
    } else {
      alert(
        "",
        "warning",
        "Warning",
        result?.message || "An error occurred. Please try again.",
      );
    }
  };

  renderSchemaInput = (dataType, value, onChange) => {
    const t = (dataType || "").toUpperCase();
    if (t.includes("BOOLEAN")) {
      return (
        <Select
          className="form-control"
          placeholder="Select boolean"
          options={[
            { value: "true", label: "true" },
            { value: "false", label: "false" },
          ]}
          value={String(value)}
          onChange={(v) => onChange({ target: { value: v } })}
          default={false}
        />
      );
    }
    if (
      t.includes("INTEGER") ||
      t.includes("DECIMAL") ||
      t.includes("FLOAT") ||
      t.includes("DOUBLE") ||
      t.includes("BIGINT")
    ) {
      return (
        <input
          type="number"
          step={t.includes("DECIMAL") || t.includes("FLOAT") ? "0.0001" : "1"}
          className="form-control"
          value={value}
          onChange={onChange}
          placeholder={`Input ${dataType}`}
        />
      );
    }
    if (t.includes("DATE")) {
      return (
        <input type="date" className="form-control" value={value} onChange={onChange} />
      );
    }
    if (t.includes("TEXT")) {
      return (
        <textarea
          className="form-control"
          rows={2}
          value={value}
          onChange={onChange}
          placeholder={`Input ${dataType}`}
        />
      );
    }
    return (
      <input
        type="text"
        className="form-control"
        value={value}
        onChange={onChange}
        placeholder={`Input ${dataType}`}
      />
    );
  };
  buildSchemaPreview = () => {
    const { schema_selected_table, schema_set_fields, schema_where_conditions } =
      this.state;
    if (!schema_selected_table || !(schema_set_fields || []).length)
      return { sql: "-", payload: {} };
    const setParts = (schema_set_fields || [])
      .filter((s) => s.field)
      .map((s) => {
        const type = this.getSchemaFieldType(schema_selected_table, s.field);
        return `${s.field} = ${this.formatSchemaValue(s.value, type)}`;
      });
    const whereParts = (schema_where_conditions || [])
      .filter((c) => c.field && c.operator)
      .map((c) => {
        if (["IS NULL", "IS NOT NULL"].includes(c.operator)) {
          return `${c.field} ${c.operator}`;
        }
        const type = this.getSchemaFieldType(schema_selected_table, c.field);
        return `${c.field} ${c.operator} ${this.formatSchemaValue(c.value, type)}`;
      });
    return {
      sql: `UPDATE ${schema_selected_table} SET ${setParts.join(", ")}${
        whereParts.length ? ` WHERE ${whereParts.join(" AND ")}` : ""
      };`,
      payload: {
        table: schema_selected_table,
        update: (schema_set_fields || []).map((s) => ({
          field: s.field,
          value: s.value,
          dataType: this.getSchemaFieldType(schema_selected_table, s.field),
        })),
        where: schema_where_conditions,
      },
    };
  };

  // ------------------------------------------------- Dynamic Update tool panel
  // PIN gate for the whole Developer Mode page - shown until unlocked.
  renderPinGate() {
    const { dynamic_pin, dynamic_pin_error } = this.state;
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <div
          className="ar-table-card p-4"
          style={{ width: 420, maxWidth: "100%" }}
        >
          <h5 className="fw-bold mb-1">Developer Mode</h5>
          <label className="pr-label text-12 mb-3 d-block">
            Enter your 6-digit PIN to access Developer Mode
          </label>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            className="form-control text-center"
            placeholder="••••••"
            style={{ letterSpacing: 8, fontSize: 20 }}
            value={dynamic_pin}
            onChange={(e) => {
              const value = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
              this.setState({ dynamic_pin: value, dynamic_pin_error: "" });
            }}
            onKeyUp={(e) => {
              if (e.key === "Enter") this.unlockPin();
            }}
          />
          {dynamic_pin_error ? (
            <div className="text-danger text-12 mt-2">{dynamic_pin_error}</div>
          ) : (
            <div className="text-secondary text-12 mt-2">
              The PIN controls access to admin tools
            </div>
          )}
          <button
            className="ar-btn-gold w-100 justify-content-center mt-3"
            onClick={this.unlockPin}
            disabled={(dynamic_pin || "").length !== 6}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  renderDynamicUpdate() {
    const {
      schema_selected_table,
      schema_set_fields,
      schema_where_conditions,
    } = this.state;

    const tables = this.getSchemaTables();
    const fields = this.getSchemaFields(schema_selected_table);
    const tableOptions = tables.map((t) => ({ value: t.tableName, label: t.tableName }));
    const fieldOptions = fields.map((f) => ({
      value: f.field,
      label: `${f.field} (${f.dataType})`,
    }));
    const preview = this.buildSchemaPreview();
    const operators = ["=", "!=", ">", "<", ">=", "<=", "LIKE", "ILIKE", "IS NULL", "IS NOT NULL"];

    return (
      <div>
        <h6 className="fw-bold mb-2">Table Main</h6>
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-4">
            <div className="rounded-3 border p-3 h-100">
              <label className="pr-label text-12 mb-1">Table</label>
              <Select
                className="form-control mb-2"
                placeholder="Select table..."
                options={tableOptions}
                value={schema_selected_table}
                onChange={(table) => {
                  const firstField = this.getSchemaFields(table)[0]?.field || "";
                  this.setState({
                    schema_selected_table: table,
                    schema_set_fields: [{ id: Date.now(), field: firstField, value: "" }],
                    schema_where_conditions: [
                      { id: Date.now(), field: firstField, operator: "=", value: "" },
                    ],
                  });
                }}
              />
              <div className="pr-label text-12">
                Available fields: <b>{fields.length}</b>
              </div>
              <div className="pr-label text-12 mt-1">
                Selected SET rows: <b>{(schema_set_fields || []).length}</b>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-8">
            <div className="rounded-3 border p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="pr-label text-12 mb-0">SET Builder</label>
                <button className="btn btn-sm btn-outline-dark" onClick={this.addSchemaSetField}>
                  + Add SET Field
                </button>
              </div>
              {(schema_set_fields || []).map((s, idx) => (
                <div className="row g-2 align-items-end mb-2" key={s.id}>
                  <div className="col-12 col-md-4">
                    <label className="pr-label text-12 mb-1">Field {idx + 1}</label>
                    <Select
                      className="form-control"
                      placeholder="Select field..."
                      options={fieldOptions}
                      value={s.field}
                      onChange={(field) =>
                        this.updateSchemaSetField(s.id, { field: field, value: "" })
                      }
                    />
                  </div>
                  <div className="col-12 col-md-7">
                    <label className="pr-label text-12 mb-1">
                      Value ({this.getSchemaFieldType(schema_selected_table, s.field)})
                    </label>
                    {this.renderSchemaInput(
                      this.getSchemaFieldType(schema_selected_table, s.field),
                      s.value,
                      (e) => this.updateSchemaSetField(s.id, { value: e.target.value }),
                    )}
                  </div>
                  <div className="col-12 col-md-1">
                    <button
                      className="btn bg-danger-light text-danger w-100"
                      disabled={(schema_set_fields || []).length <= 1}
                      onClick={() => this.removeSchemaSetField(s.id)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="fw-bold mb-0">WHERE Builder</h6>
          <button className="btn btn-sm btn-outline-dark" onClick={this.addSchemaWhereCondition}>
            + Add Condition
          </button>
        </div>
        <div className="rounded-3 border p-2 mb-4">
          {(schema_where_conditions || []).map((c, idx) => (
            <div className="row g-2 align-items-end mb-2" key={c.id}>
              <div className="col-12 col-md-4">
                <label className="pr-label text-12 mb-1">Field {idx + 1}</label>
                <Select
                  className="form-control"
                  placeholder="Select field..."
                  options={fieldOptions}
                  value={c.field}
                  onChange={(field) =>
                    this.updateSchemaWhereCondition(c.id, { field: field, value: "" })
                  }
                />
              </div>
              <div className="col-12 col-md-2">
                <label className="pr-label text-12 mb-1">Operator</label>
                <Select
                  className="form-control"
                  placeholder="Select operator..."
                  options={operators.map((op) => ({ value: op, label: op }))}
                  value={c.operator}
                  onChange={(v) =>
                    this.updateSchemaWhereCondition(c.id, {
                      operator: v,
                      value: ["IS NULL", "IS NOT NULL"].includes(v)
                        ? ""
                        : c.value,
                    })
                  }
                />
              </div>
              <div className="col-12 col-md-5">
                <label className="pr-label text-12 mb-1">
                  Value ({this.getSchemaFieldType(schema_selected_table, c.field)})
                </label>
                {["IS NULL", "IS NOT NULL"].includes(c.operator) ? (
                  <input className="form-control" value="(not required)" disabled />
                ) : (
                  this.renderSchemaInput(
                    this.getSchemaFieldType(schema_selected_table, c.field),
                    c.value,
                    (e) => this.updateSchemaWhereCondition(c.id, { value: e.target.value }),
                  )
                )}
              </div>
              <div className="col-12 col-md-1">
                <button
                  className="btn bg-danger-light text-danger w-100"
                  disabled={(schema_where_conditions || []).length <= 1}
                  onClick={() => this.removeSchemaWhereCondition(c.id)}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
        <h6 className="fw-bold mb-2">Summary Before Submit</h6>
        <div className="rounded-3 border p-3 mb-2">
          <div className="pr-label text-12 mb-1">SQL Preview</div>
          <code>{preview.sql}</code>
        </div>
        <div className="rounded-3 border p-3 mb-3">
          <div className="pr-label text-12 mb-1">Payload Preview</div>
          <pre className="mb-0 text-12">{JSON.stringify(preview.payload, null, 2)}</pre>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-light"
            onClick={() => {
              localStorage.removeItem("dev-mode-unlock-at");
              this.setState({ pin_unlocked: false });
            }}
          >
            Lock
          </button>
          <button
            className="ar-btn-gold"
            onClick={this.submitDynamicUpdate}
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  renderComingSoon(tool) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center text-center"
        style={{ minHeight: 300 }}
      >
        <h6 className="fw-bold mb-1">{tool.name}</h6>
        <div className="text-secondary text-14">Under development — coming soon</div>
      </div>
    );
  }

  renderToolContent() {
    const tool = DEV_TOOLS.find((t) => t.key === this.state.selected_tool);
    if (!tool) return null;
    if (tool.key === "dynamic-update") return this.renderDynamicUpdate();
    if (tool.key === "settlement-hq-approve") return <SettlementHqApprove filterSlot={this.state.filter_slot} />;
    if (tool.key === "user-impersonation") return <UserImpersonation filterSlot={this.state.filter_slot} />;
    return this.renderComingSoon(tool);
  }

  // ช่องใน hero ให้เครื่องมือ portal กล่อง Filter ของตัวเองขึ้นมา (state ของ filter ยังอยู่ในเครื่องมือ)
  setFilterSlot = (node) => {
    if (node !== this.state.filter_slot) this.setState({ filter_slot: node });
  };

  render() {
    const { selected_tool, pin_unlocked } = this.state;
    const activeTool = DEV_TOOLS.find((t) => t.key === selected_tool);

    if (!pin_unlocked) {
      return (
        <div className="w-100 min-vh-100 premium-report pr-scope pu-scope">
          <Navbar />
          <div className="body p-3 mt-3">{this.renderPinGate()}</div>
        </div>
      );
    }

    return (
      <div className="w-100 min-vh-100 premium-report pr-scope pu-scope">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* Header */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Developer</div>
                <div className="ar-title">Developer Mode</div>
                <div className="ar-subtitle">Admin tools — use with caution</div>
              </div>
              {pin_unlocked && (
                <div className="ar-hero-actions">
                  {/* แท็บเครื่องมือ (แบบเดียวกับแท็บ Deposit / Withdrawals หน้า Webhook Logs) */}
                  <div className="pu-tabs pu-tabs-dark mb-0">
                    {VISIBLE_TOOLS.map((tool) => (
                      <button
                        type="button"
                        key={tool.key}
                        className={`pu-tab ${tool.key === selected_tool ? "active" : ""}`}
                        onClick={() => this.setState({ selected_tool: tool.key })}
                      >
                        <span className="icon me-2">{tool.icon}</span>
                        {tool.name}
                        {tool.comingSoon ? <span className="badge rounded-pill ms-2 bg-secondary">soon</span> : tool.locked ? <span className="text-12 ms-2">🔒</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Filter ของเครื่องมือที่เลือกจะถูก portal มาวางตรงนี้ */}
            <div ref={this.setFilterSlot} />
          </div>

          {/* Content: เครื่องมือที่เลือก (แท็บอยู่ในหัว hero) — เครื่องมือที่มีตารางของตัวเองไม่ต้องห่อการ์ดซ้อน */}
          {activeTool?.ownTable ? (
            this.renderToolContent()
          ) : (
            <div className="ar-table-card p-3">
              <div className="mb-3">
                <h5 className="fw-bold mb-1">{activeTool?.name}</h5>
                <div className="text-secondary text-14">{activeTool?.desc}</div>
              </div>
              {this.renderToolContent()}
            </div>
          )}
        </div>
      </div>
    );
  }
}
